import React from 'react';
import reconciliationService from '../services/reconciliationService';
import type { ReconciliationSummaryResponse } from '../types/reconciliation';

export interface UseReconciliationStatusResult {
  data: ReconciliationSummaryResponse | null;
  loading: boolean;
  error: string | null;
  /** Hits the cache; if cached value is fresh nothing else happens. */
  refresh: () => void;
  /** Bypasses the cache. */
  forceRefresh: () => void;
}

/**
 * Auto-fetches the reconciliation summary on mount. The underlying service
 * caches results for 5 minutes, so navigating back to the page that uses
 * this hook within that window is free.
 */
export function useReconciliationStatus(): UseReconciliationStatusResult {
  const [data, setData] = React.useState<ReconciliationSummaryResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const cancelledRef = React.useRef(false);

  const fetchOnce = React.useCallback(async (force: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await reconciliationService.getSummary(force);
      if (cancelledRef.current) return;
      setData(resp);
    } catch (e: any) {
      if (cancelledRef.current) return;
      setError(e?.message || 'Failed to fetch reconciliation summary');
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    cancelledRef.current = false;
    fetchOnce(false);
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchOnce]);

  const refresh = React.useCallback(() => { fetchOnce(false); }, [fetchOnce]);
  const forceRefresh = React.useCallback(() => { fetchOnce(true); }, [fetchOnce]);

  return { data, loading, error, refresh, forceRefresh };
}
